import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CALCULATORS } from "@/data/calculators";
import CalculatorCard from "@/components/CalculatorCard";
import CategoryFilter from "@/components/CategoryFilter";
import SearchBar from "@/components/SearchBar";
import Seo from "@/components/Seo";
import { Sparkles } from "lucide-react";

export default function Home() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialCat = new URLSearchParams(location.search).get("cat") || "all";
  const [cat, setCat] = useState(initialCat);
  const [q, setQ] = useState("");

  useEffect(() => {
    const c = new URLSearchParams(location.search).get("cat") || "all";
    setCat(c);
  }, [location.search]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return CALCULATORS.filter((c) => {
      const catOk = cat === "all" || c.category === cat;
      if (!query) return catOk;
      const hay = [c.name, c.category, c.description, ...(c.keywords || [])].join(" ").toLowerCase();
      return catOk && hay.includes(query);
    });
  }, [cat, q]);

  return (
    <>
      <Seo
        title="Fitness Calculator — 32 Free Health & Fitness Tools"
        description="Free, instant fitness & health calculators — BMI, BMR, TDEE, macros, pregnancy, heart rate and more. Mobile-friendly. No sign-up."
        path="/"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Fitness Calculator",
            url: window.location.origin,
            potentialAction: {
              "@type": "SearchAction",
              target: `${window.location.origin}/?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 grid-bg" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <Sparkles className="h-3 w-3" />
              32 free tools · instant results · no sign-up
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              All the Health & Fitness Calculators
              <br className="hidden sm:block" /> you need — <span className="italic font-medium text-muted-foreground">in one place.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
              From BMI and TDEE to pregnancy tracking and 1RM — clean, accurate calculators with a friendly modern feel. Runs entirely in your browser.
            </p>
            <div className="mt-8 max-w-xl">
              <SearchBar
                onPick={(slug) => navigate(`/calculators/${slug}`)}
                testIdPrefix="hero"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filter + grid */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold">Browse all calculators</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {filtered.length} of {CALCULATORS.length} tools shown
            </p>
          </div>
          <div className="max-w-md w-full sm:w-72">
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter this list…"
              data-testid="grid-filter-input"
              className="h-10 w-full rounded-full border border-border bg-background px-4 text-sm outline-none focus:ring-2 focus:ring-ring/40"
            />
          </div>
        </div>

        <CategoryFilter value={cat} onChange={(v) => { setCat(v); navigate(v === "all" ? "/" : `/?cat=${v}`, { replace: true }); }} />

        {filtered.length === 0 ? (
          <div
            data-testid="empty-state"
            className="mt-14 text-center py-16 rounded-2xl border border-dashed border-border"
          >
            <p className="text-lg font-medium">No calculators match your search.</p>
            <p className="text-sm text-muted-foreground mt-2">Try a different keyword or pick another category.</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((c) => (
              <CalculatorCard key={c.slug} calc={c} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}
